import { useSelector } from 'react-redux'
import { FlexColumn, FlexRow } from '../../atoms/layout/Flex'
import Rating from '../../atoms/inputs/Rating'
import Label from '../../atoms/display/Label'
import IconButton from '../../atoms/inputs/IconButton'
import { DeleteIcon, EditIcon, EvaluationIcon } from '../../atoms/display/Icons'
import Table from '../../atoms/display/Table'
import TableRow from '../../atoms/display/TableRow'
import TableCell from '../../atoms/display/TableCell'
import SectionPanel from '../../molecules/SectionPanel'
import {
  useClientEvaluationCommentDeleteDialog,
  useClientEvaluationCommentEditDialog,
  useClientEvaluationDialog,
} from '../../../hooks/useDialog'
import { ClientDetail } from '../../../types/Company'
import useDay from '../../../hooks/useDay'
import { SessionSelectors } from '../../../store/session/selectors'

type Props = {
  clientDetail: ClientDetail
}

export default function ClientEvaluationView({ clientDetail }: Props) {
  const clientEvaluationDialog = useClientEvaluationDialog()
  const clientEvaluationCommentEditDialog = useClientEvaluationCommentEditDialog()
  const clientEvaluationCommentDeleteDialog = useClientEvaluationCommentDeleteDialog()
  const { formatDate } = useDay()
  const user = useSelector(SessionSelectors.user)

  const handleEvaluationDialog = () => {
    if (clientDetail.id) {
      clientEvaluationDialog.open(clientDetail.id)
    }
  }

  return (
    <FlexColumn>
      <FlexRow align="center">
        <Label text="総合" />
        <Rating
          max={3}
          value={clientDetail.evaluation?.totalPoint}
          label={`${clientDetail.evaluation?.totalPoint.toFixed(1) ?? '--'}`}
          readOnly
        />
        <IconButton label="編集" onClick={handleEvaluationDialog}>
          <EvaluationIcon size={24} />
        </IconButton>
      </FlexRow>
      <Table>
        <TableRow>
          <TableCell size="sm3">
            <Label text="連絡の速さ・まめさ" />
          </TableCell>
          <TableCell size="sm3">
            <Rating
              max={3}
              value={clientDetail.evaluation?.communicationSpeedPoint}
              label={`${clientDetail.evaluation?.communicationSpeedPoint ?? ''}`}
              readOnly
            />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell size="sm3">
            <Label text="イレギュラー対応" />
          </TableCell>
          <TableCell size="sm3">
            <Rating
              max={3}
              value={clientDetail.evaluation?.irregularitiesResponsePoint}
              label={`${clientDetail.evaluation?.irregularitiesResponsePoint ?? ''}`}
              readOnly
            />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell size="sm3">
            <Label text="予算" />
          </TableCell>
          <TableCell size="sm3">
            <Rating
              max={3}
              value={clientDetail.evaluation?.budgetPoint}
              label={`${clientDetail.evaluation?.budgetPoint ?? ''}`}
              readOnly
            />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell size="sm3">
            <Label text="工期を守っているか" />
          </TableCell>
          <TableCell size="sm3">
            <Rating
              max={3}
              value={clientDetail.evaluation?.constructionSchedulePoint}
              label={`${clientDetail.evaluation?.constructionSchedulePoint ?? ''}`}
              readOnly
            />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell size="sm3">
            <Label text="技術" />
          </TableCell>
          <TableCell size="sm3">
            <Rating
              max={3}
              value={clientDetail.evaluation?.technologyPoint}
              label={`${clientDetail.evaluation?.technologyPoint ?? ''}`}
              readOnly
            />
          </TableCell>
        </TableRow>
      </Table>
      <SectionPanel title="コメント" size="full">
        <FlexColumn space={2}>
          {clientDetail.evaluation?.comments.map((comment) => (
            <FlexColumn key={comment.id} space={0}>
              <FlexRow align="flex-end">
                <Label text={comment.user.name} />
                <Label text={formatDate(comment.createdAt)} variant="caption" />
                {comment.user.id === user?.id && (
                  <>
                    <IconButton
                      label="編集"
                      size="sm"
                      onClick={() =>
                        clientEvaluationCommentEditDialog.open(clientDetail.id, comment.id, comment.comment)
                      }
                    >
                      <EditIcon size={16} />
                    </IconButton>
                    <IconButton
                      label="削除"
                      size="sm"
                      onClick={() =>
                        clientEvaluationCommentDeleteDialog.open(clientDetail.id, comment.id, comment.comment)
                      }
                    >
                      <DeleteIcon size={16} />
                    </IconButton>
                  </>
                )}
              </FlexRow>
              <Label text={comment.comment} whiteSpace="pre-wrap" />
            </FlexColumn>
          ))}
        </FlexColumn>
      </SectionPanel>
    </FlexColumn>
  )
}
